/**
 * Telling the browser — and the password managers — to leave a search box
 * alone.
 *
 * ## The bug
 *
 * Clicking into the assignee picker raised Chrome's own address dropdown over
 * the panel: the reader's saved email addresses and a "Manage Addresses…"
 * row, sitting on top of the list of people they were actually choosing from
 * (user, 2026-09-11). Two suggestion lists stacked on each other, and the one
 * on top offers to type the reader's own email address into a field that
 * searches a project directory.
 *
 * The trigger is the placeholder. Chrome classifies a field by its label,
 * name and placeholder text, and this one reads "Type a name, company, or
 * email…" — so it is read as an email field, and email fields get the
 * address profile.
 *
 * ## Why not just `autocomplete="off"`
 *
 * Because Chrome does not honour it here. `off` is respected for a field
 * Chrome has no profile data for, and overridden for the address and payment
 * classes precisely so that a site cannot opt its users out of autofill —
 * which is a good rule for a checkout page and the wrong one for a combobox
 * that has its own suggestion list. Firefox and Safari do honour it.
 *
 * `new-password` is the lever that works on all three, because no browser
 * fills a new-password field from an address profile. It is a lie about what
 * the field holds, which is why this lives behind a name that says what it is
 * for rather than being typed at each call site — and why the two `data-*`
 * opt-outs below matter: they are what stop the password managers from
 * reading `new-password` at face value and offering to generate one.
 *
 * There is no `<form>` around any of these boxes and none of them is
 * `type="password"`, so nothing here can raise a save-password prompt or
 * Safari's strong-password sheet — both of those need a password field.
 *
 * ## Use it on search boxes, never on a real field
 *
 * This belongs on a box that searches something the browser cannot know
 * about: a project directory, a location tree, a photo library. It must NOT
 * go on a field where autofill is a kindness — `Invite`'s email box is the
 * one in this console, and a teammate's address is exactly what the browser
 * should offer there.
 */
export const NO_AUTOFILL = {
  /** See above: the one value every browser declines to profile-fill. */
  autoComplete: "new-password",
  /** 1Password and Bitwarden. */
  "data-1p-ignore": true,
  "data-bwignore": true,
  /** LastPass, which wants the string. */
  "data-lpignore": "true",
  /** Dashlane — "other" is its opt-out, not a field kind. */
  "data-form-type": "other",
} as const;
